import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useBranding } from '@/lib/branding';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, LogIn, AlertCircle, ChefHat } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const sessionExpired = searchParams.get('session') === 'expired';

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || (lang === 'es' ? 'Email o contraseña incorrectos' : 'Email ou senha incorretos');
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))] p-4 app-noise">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-3">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="logo" className="h-14 w-14 rounded-2xl object-cover shadow-lg" />
            ) : (
              <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--primary))] grid place-items-center shadow-lg">
                <ChefHat className="h-8 w-8 text-[hsl(var(--primary-foreground))]" />
              </div>
            )}
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">{branding?.nombre || 'Restaurante'}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            {lang === 'es' ? 'Sistema de gestión PGC PYMES' : 'Sistema de gestão PGC PYMES'}
          </p>
        </div>

        <Card className="shadow-xl">
          <CardContent className="p-6">
            <h2 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
              <LogIn className="h-5 w-5 text-[hsl(var(--primary))]" />
              {lang === 'es' ? 'Iniciar sesión' : 'Entrar'}
            </h2>

            {sessionExpired && (
              <Alert className="mb-4 border-amber-200 bg-amber-50 text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {lang === 'es' ? 'Tu sesión expiró. Vuelve a iniciar.' : 'Sua sessão expirou. Entre novamente.'}
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive" className="mb-4" data-testid="login-error-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="login-email">{lang === 'es' ? 'Email' : 'E-mail'}</Label>
                <Input
                  id="login-email"
                  type="email"
                  required
                  autoComplete="username"
                  placeholder="admin@restaurante.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="login-password">{lang === 'es' ? 'Contraseña' : 'Senha'}</Label>
                <Input
                  id="login-password"
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  data-testid="login-password-input"
                />
              </div>
              <Button type="submit" className="w-full" size="lg" disabled={loading || !email || !password} data-testid="login-submit-button">
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {lang === 'es' ? 'Entrando...' : 'Entrando...'}
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4 mr-2" />
                    {lang === 'es' ? 'Iniciar sesión' : 'Entrar'}
                  </>
                )}
              </Button>
            </form>

            <div className="flex justify-center gap-2 mt-4 text-xs">
              <button
                type="button"
                onClick={() => setLang('es')}
                className={lang === 'es' ? 'font-semibold' : 'opacity-50 hover:opacity-100'}
              >🇪🇸 ES</button>
              <span className="opacity-30">|</span>
              <button
                type="button"
                onClick={() => setLang('pt')}
                className={lang === 'pt' ? 'font-semibold' : 'opacity-50 hover:opacity-100'}
              >🇧🇷 PT</button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-[hsl(var(--muted-foreground))] mt-4">
          © {new Date().getFullYear()} • Powered by <a href="https://www.tstt.com.br" target="_blank" rel="noreferrer" className="underline">TSTT</a>
        </p>
      </div>
    </div>
  );
}
